import mammoth from 'mammoth';
import * as pdfjsLib from 'pdfjs-dist';
import JSZip from 'jszip';

// Configurar PDF.js para el navegador - usar worker local para evitar problemas de caché
pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.js',
  import.meta.url
).toString();

export interface ProcessedFile {
  name: string;
  path: string;
  size: number;
  type: string;
  content: string;
  extractedNif?: string;
  success: boolean;
  error?: string;
}

export interface FileProcessingResult {
  success: boolean;
  processedFiles: ProcessedFile[];
  totalFiles: number;
  successfulFiles: number;
  errorFiles: number;
  errors: string[];
}

export class FileProcessor {
  private static readonly SUPPORTED_EXTENSIONS = ['.pdf', '.docx', '.doc', '.odt', '.txt'];
  private static readonly NIF_PATTERNS = [
    /[0-9]{8}[TRWAGMYFPDXBNJZSQVHLCKE]/gi, // NIF estándar
    /[XYZ][0-9]{7}[TRWAGMYFPDXBNJZSQVHLCKE]/gi, // NIE
    /[0-9]{8}-[TRWAGMYFPDXBNJZSQVHLCKE]/gi, // NIF con guión
    /[XYZ][0-9]{7}-[TRWAGMYFPDXBNJZSQVHLCKE]/gi, // NIE con guión
  ];
  private static readonly CIF_PATTERNS = [
    /[ABCDEFGHJNPQRSUVW][0-9]{7}[0-9A-J]/gi, // CIF estándar: letra + 7 dígitos + dígito de control
    /[ABCDEFGHJNPQRSUVW][0-9]{8}/gi, // CIF con 8 dígitos (sin dígito de control)
  ];

  static isFileSupported(fileName: string): boolean {
    const extension = fileName.toLowerCase().split('.').pop();
    return this.SUPPORTED_EXTENSIONS.some(ext => ext === `.${extension}`);
  }

  static hasNifInFilename(fileName: string): boolean {
    const allPatterns = [...this.NIF_PATTERNS, ...this.CIF_PATTERNS];
    return allPatterns.some(pattern => pattern.test(fileName));
  }

  static async processFiles(files: File[], nifDetectionMode: 'filename' | 'content' = 'content'): Promise<FileProcessingResult> {
    const result: FileProcessingResult = {
      success: true,
      processedFiles: [],
      totalFiles: files.length,
      successfulFiles: 0,
      errorFiles: 0,
      errors: []
    };

    for (const file of files) {
      // Verificar si el archivo debe ser procesado según el modo de detección
      let shouldProcess = false;
      let skipReason = '';

      if (nifDetectionMode === 'filename') {
        // En modo filename, solo procesar archivos que tengan NIF/CIF en el nombre
        if (this.hasNifInFilename(file.name)) {
          shouldProcess = true;
        } else {
          skipReason = 'No contiene NIF/CIF en el nombre del archivo';
        }
      } else {
        // En modo content, solo procesar archivos con formatos soportados
        if (this.isFileSupported(file.name)) {
          shouldProcess = true;
        } else {
          const extension = file.name.toLowerCase().split('.').pop();
          skipReason = `Formato no soportado (.${extension})`;
        }
      }

      if (shouldProcess) {
        try {
          const processedFile = await this.processFile(file, nifDetectionMode);
          result.processedFiles.push(processedFile);
          
          if (processedFile.success) {
            result.successfulFiles++;
          } else {
            result.errorFiles++;
            result.errors.push(`${file.name}: ${processedFile.error}`);
          }
        } catch (error: any) {
          result.errorFiles++;
          result.errors.push(`${file.name}: ${error.message}`);
          result.processedFiles.push({
            name: file.name,
            path: file.name,
            size: file.size,
            type: file.type,
            content: '',
            success: false,
            error: error.message
          });
        }
      } else {
        // Agregar archivo no procesado a la lista
        result.processedFiles.push({
          name: file.name,
          path: file.name,
          size: file.size,
          type: file.type,
          content: '',
          success: false,
          error: skipReason
        });
      }
    }

    result.success = result.errorFiles === 0;
    return result;
  }

  private static async processFile(file: File, nifDetectionMode: 'filename' | 'content' = 'content'): Promise<ProcessedFile> {
    const extension = file.name.toLowerCase().split('.').pop();
    let content = '';
    let extractedNif: string | undefined;

    try {
      if (nifDetectionMode === 'filename') {
        // En modo filename, extraer NIF/CIF del nombre del archivo
        extractedNif = this.extractNifFromFilename(file.name);
        if (!extractedNif) {
          throw new Error('No se encontró NIF/CIF en el nombre del archivo');
        }
        // No necesitamos extraer contenido para modo filename
        content = '';
      } else {
        // En modo content, extraer contenido y buscar NIF/CIF
        switch (`.${extension}`) {
          case '.pdf':
            content = await this.extractPdfContent(file);
            break;
          case '.docx':
            content = await this.extractDocxContent(file);
            break;
          case '.doc':
            // Para archivos .doc, intentamos leer como texto plano
            content = await this.extractTextContent(file);
            break;
          case '.odt':
            content = await this.extractOdtContent(file);
            break;
          case '.txt':
            content = await this.extractTextContent(file);
            break;
          default:
            throw new Error(`Tipo de archivo no soportado: .${extension}`);
        }

        extractedNif = this.extractNifFromContent(content);
        if (!extractedNif) {
          throw new Error('No se encontró NIF/CIF en el contenido del archivo');
        }
      }

      return {
        name: file.name,
        path: file.name,
        size: file.size,
        type: file.type,
        content,
        extractedNif,
        success: true
      };
    } catch (error: any) {
      return {
        name: file.name,
        path: file.name,
        size: file.size,
        type: file.type,
        content: '',
        success: false,
        error: error.message
      };
    }
  }

  private static async extractPdfContent(file: File): Promise<string> {
    try {
      const arrayBuffer = await file.arrayBuffer();
      const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
      let text = '';

      for (let i = 1; i <= pdf.numPages; i++) {
        const page = await pdf.getPage(i);
        const textContent = await page.getTextContent();
        const pageText = textContent.items
          .map((item: any) => item.str)
          .join(' ');
        text += pageText + '\n';
      }

      return text;
    } catch (error: any) {
      console.error(`Error procesando PDF ${file.name}:`, error);
      throw error;
    }
  }

  private static async extractDocxContent(file: File): Promise<string> {
    const arrayBuffer = await file.arrayBuffer();
    const result = await mammoth.extractRawText({ arrayBuffer });
    return result.value;
  }

  private static async extractOdtContent(file: File): Promise<string> {
    try {
      const arrayBuffer = await file.arrayBuffer();
      const zip = new JSZip();
      const zipFile = await zip.loadAsync(arrayBuffer);
      
      // ODT es un archivo ZIP que contiene content.xml
      const contentXml = await zipFile.file('content.xml')?.async('text');
      if (!contentXml) {
        throw new Error('No se pudo encontrar content.xml en el archivo ODT');
      }

      // Extraer texto del XML (simplificado)
      const text = contentXml
        .replace(/<[^>]*>/g, ' ') // Remover tags XML
        .replace(/\s+/g, ' ') // Normalizar espacios
        .trim();

      return text;
    } catch (error: any) {
      console.error(`Error procesando ODT ${file.name}:`, error);
      // Si falla la extracción ODT, intentar como texto plano
      return await this.extractTextContent(file);
    }
  }

  private static async extractTextContent(file: File): Promise<string> {
    return await file.text();
  }

  private static extractNifFromContent(content: string): string | undefined {
    // Buscar NIF primero
    for (const pattern of this.NIF_PATTERNS) {
      const matches = content.match(pattern);
      if (matches && matches.length > 0) {
        const nif = matches[0].replace(/-/g, '').toUpperCase();
        if (this.validateNif(nif)) {
          return nif;
        }
      }
    }

    // Si no se encuentra NIF, buscar CIF
    for (const pattern of this.CIF_PATTERNS) {
      const matches = content.match(pattern);
      if (matches && matches.length > 0) {
        return matches[0].toUpperCase();
      }
    }

    return undefined;
  }

  private static extractNifFromFilename(filename: string): string | undefined {
    // Buscar CIF primero
    for (const pattern of this.CIF_PATTERNS) {
      const matches = filename.match(pattern);
      if (matches && matches.length > 0) {
        const cif = matches[0].toUpperCase();
        if (this.validateCif(cif)) {
          return cif;
        }
      }
    }
  
    // Luego buscar NIF/NIE
    for (const pattern of this.NIF_PATTERNS) {
      const matches = filename.match(pattern);
      if (matches && matches.length > 0) {
        const nif = matches[0].replace(/-/g, '').toUpperCase();
        if (this.validateNif(nif)) {
          return nif;
        }
      }
    }

    return undefined;
  }

  private static validateNif(nif: string): boolean {
    if (nif.length !== 9) return false;
    
    const numbers = nif.substring(0, 8);
    const letter = nif.substring(8, 9);
    
    if (!/^\d{8}$/.test(numbers)) return false;
    
    const letterIndex = parseInt(numbers) % 23;
    const validLetters = 'TRWAGMYFPDXBNJZSQVHLCKE';
    
    return validLetters[letterIndex] === letter;
  }

  private static validateCif(cif: string): boolean {
    if (cif.length !== 9) return false;

    const firstLetter = cif.substring(0, 1);
    const body = cif.substring(1, 8);
    const control = cif.substring(8);

    // Verificar que la letra inicial sea válida
    if (!/[ABCDEFGHJNPQRSUVW]/.test(firstLetter)) return false;

    // El cuerpo debe ser exactamente 7 dígitos
    if (!/^\d{7}$/.test(body)) return false;

    const digits = body.split('').map(Number);
    const uniqueDigits = new Set(digits).size;
    if (uniqueDigits < 3) return false;

    // Control puede ser dígito o letra A-J
    if (!/^[0-9A-J]$/.test(control)) return false;

    return true;
  }
}